import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import { Dealer, EditDealer } from '@/types'
import DealersApi from '@/api/dealers'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'

type DealerState = {
  errorUsername: string | null
  dealerEditMode: boolean
  dealers: Dealer[]
  currentDealer: Dealer | undefined
  dealersMarkets: string[]
  dealerSearchMode: boolean
  startingItem: number
  totalItems: number
  itemsPerPage: number
  searchParams: { search: string | undefined; searchFields: string | undefined }
  sortParams: { sortField: string; sortType: string }
}

type FetchDealerParams = {
  from: number
  size: number
  sortField: DealerState['sortParams']['sortField']
  sortType: DealerState['sortParams']['sortType']
  search?: DealerState['searchParams']['search']
  searchFields?: DealerState['searchParams']['searchFields']
}

export const useDealersStore = defineStore('dealers', {
  state: (): DealerState => ({
    errorUsername: null,
    dealerEditMode: false,
    dealers: [],
    currentDealer: undefined,
    dealersMarkets: ['ITA', 'USA'],
    dealerSearchMode: false,
    startingItem: 0,
    totalItems: 0,
    itemsPerPage: 20,
    searchParams: {
      search: undefined,
      searchFields: undefined
    },
    sortParams: {
      sortField: 'username',
      sortType: 'asc'
    }
  }),
  getters: {
    discountPercentage: state => {
      const authStore = useAuthStore()
      return state.currentDealer?.discountPercentage || authStore.userData?.discountPercentage || 0
    },
    chargePercentage: state => {
      const authStore = useAuthStore()
      return state.currentDealer?.chargePercentage || authStore.userData?.chargePercentage || 0
    }
  },
  actions: {
    async fetchDealers(from: number, size: number) {
      const appStore = useAppStore()
      this.setDealerSearchMode(false)
      try {
        appStore.setIsFetching(true)
        const params: FetchDealerParams = {
          from: from,
          size: size,
          ...this.sortParams
        }
        if (this.searchParams.search !== undefined && this.searchParams.searchFields !== undefined) {
          params.search = this.searchParams.search
          params.searchFields = this.searchParams.searchFields
        }
        const data = await DealersApi.get(params)
        this.dealers = data.entities
        this.totalItems = data.resultCount
        appStore.setIsFetching(false)
      } catch (e) {
        const error = e as Error
        appStore.error = error.message
        appStore.setIsFetching(false)
      }
    },
    async searchDealers(filt: string | undefined, field: string | undefined) {
      const appStore = useAppStore()
      appStore.setIsFetching(true)
      try {
        this.searchParams.searchFields = field
        this.searchParams.search = filt
        this.startingItem = 0
        await this.fetchDealers(this.startingItem, this.itemsPerPage)
        appStore.setIsFetching(false)
        this.setDealerSearchMode(true)
      } catch (e) {
        const error = e as Error
        appStore.error = error.message
        appStore.setIsFetching(false)
      }
    },
    async sortDealers(field: string) {
      if (this.sortParams.sortField === field) {
        this.sortParams.sortType = this.sortParams.sortType === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortParams.sortField = field
      }
      const appStore = useAppStore()
      appStore.setIsFetching(true)
      try {
        this.fetchDealers(this.startingItem, this.itemsPerPage)
        appStore.setIsFetching(false)
      } catch (e) {
        appStore.setIsFetching(false)
        const error = e as AxiosError
        appStore.error = error.message
      }
    },
    async create(dealer: Dealer) {
      const appStore = useAppStore()
      appStore.setIsFetching(true)
      try {
        await DealersApi.create(dealer)
        appStore.setIsFetching(false)
        return true
      } catch (e) {
        appStore.setIsFetching(false)
        const error = e as AxiosError
        if (
          error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'message' in error.response.data
        ) {
          const message = (error.response.data as { message: string }).message
          if (message === 'CREATE_DEALER_INVALID_USER_ALREADY_EXIST') this.errorUsername = message
          else appStore.error = error.message
        }
        return false
      }
    },
    resetDealers() {
      this.dealers = []
    },
    setDealerEditMode(value: boolean) {
      this.dealerEditMode = value
    },
    async updateDealer(username: string, dealer: EditDealer) {
      const appStore = useAppStore()
      try {
        appStore.setIsFetching(true)
        const data = await DealersApi.updateDealer(username, dealer)
        this.dealers = this.dealers.map(d => {
          if (d.username === username) return { ...data }
          else return d
        })
        appStore.setIsFetching(false)
        this.dealerEditMode = false
      } catch (e) {
        const error = e as Error
        appStore.error = error.message
        appStore.setIsFetching(false)
      }
    },
    async updateDealerToggle(username: string, toggle: string, value: boolean) {
      const appStore = useAppStore()
      const body: Record<string, boolean> = {}
      body[toggle] = !value
      try {
        appStore.setIsFetching(true)
        const data = await DealersApi.updateDealer(username, body)
        this.dealers = this.dealers.map(d => {
          if (d.username === username) return { ...data }
          else return d
        })
        appStore.setIsFetching(false)
      } catch (e) {
        const err = e as Error
        appStore.error = err.message
        appStore.setIsFetching(false)
      }
    },
    async deleteDealer(username: string) {
      const appStore = useAppStore()
      appStore.setIsFetching(true)
      try {
        await DealersApi.deleteDealer(username)
        appStore.setIsFetching(false)
      } catch (e) {
        const error = e as Error
        appStore.error = error.message
        appStore.setIsFetching(false)
      }
    },
    setDealerSearchMode(value: boolean) {
      this.dealerSearchMode = value
    },
    async getDealer(username: string) {
      const appStore = useAppStore()
      appStore.setIsFetching(true)
      try {
        this.currentDealer = await DealersApi.getDealer(username)
        appStore.setIsFetching(false)
      } catch (e) {
        const error = e as Error
        appStore.error = error.message
        appStore.setIsFetching(false)
      }
    },
    async getDealerByToken(token: string) {
      const appStore = useAppStore()
      appStore.setIsFetching(true)
      try {
        this.currentDealer = await DealersApi.getDealerByToken(token)
        appStore.setIsFetching(false)
      } catch (e) {
        const error = e as Error
        appStore.error = error.message
        appStore.setIsFetching(false)
      }
    }
  }
})
